// Combined preprocess stage for extraction and loop expansion.
// Keeps compiler pipeline compact and reduces full-source passes.
import type { LuisArrayVariables, LuisObjectVariables } from '../types'
import type { LuisCompileMode } from '../types'
import type { LuisCompileWarning } from '../types'
import { expandForLoops } from '../loops/for-loop'
import { assertNoLegacyControlClosers } from './legacy-control-closers'
import { extractSystemVariables } from '../variables/extract-system-variables'
import { expandLuisFunctions } from '../functions'
import type { LuisResourceLimits, LuisResourcePolicy } from '../resource-limits'
import { DEFAULT_LUIS_RESOURCE_LIMITS } from '../resource-limits'
import { mergeExtractedVariables } from './merge-extracted-variables'

// Shared preprocess payload consumed by later compiler stages.
export type PreprocessedLuisSource = {
  source: string
  variables: Record<string, string>
  classVariables: Record<string, string>
  objectVariables: LuisObjectVariables
  arrayVariables: LuisArrayVariables
  warnings: LuisCompileWarning[]
}

// Extract system variables and expand loop blocks in one preprocess stage.
export function preprocessLuisSource(
  source: string,
  collectWarnings = true,
  mode: LuisCompileMode = 'full',
  options?: {
    sourceId: string
    resourcePolicy: LuisResourcePolicy
    resourceLimits: LuisResourceLimits
  },
): PreprocessedLuisSource {
  // Keep vendor-fast mode free from function/loop expansion overhead.
  if (mode === 'vendor-fast') {
    const extractedFast = extractSystemVariables(source, collectWarnings, options)
    assertNoLegacyControlClosers(extractedFast.cleanSource)
    // Return extracted payload without control-flow expansion.
    return {
      source: extractedFast.cleanSource,
      variables: extractedFast.variables,
      classVariables: extractedFast.classVariables,
      objectVariables: extractedFast.objectVariables,
      arrayVariables: extractedFast.arrayVariables,
      warnings: extractedFast.warnings,
    }
  } else {
    // Continue full preprocess pipeline for Lenty control syntax.
  }

  const functionExpanded = shouldRunFunctionExpansion(source)
    ? expandLuisFunctions(source, collectWarnings)
    : { source, warnings: [] }
  const extracted = extractSystemVariables(functionExpanded.source, collectWarnings, options)
  assertNoLegacyControlClosers(extracted.cleanSource)
  const loopWarnings: LuisCompileWarning[] = []
  const sourceWithLoops = shouldRunLoopExpansion(extracted.cleanSource)
    ? expandForLoops(extracted.cleanSource, {
      arrays: extracted.arrayVariables,
      variables: extracted.variables,
      objectVariables: extracted.objectVariables,
      sourceId: options?.sourceId ?? '<inline>',
      resourcePolicy: options?.resourcePolicy ?? 'fail-closed',
      resourceLimits: options?.resourceLimits ?? DEFAULT_LUIS_RESOURCE_LIMITS,
      warnings: loopWarnings,
    })
    : extracted.cleanSource
  const loopExtracted = extractSystemVariables(sourceWithLoops, collectWarnings, options)
  const mergedVariables = mergeExtractedVariables(
    {
      variables: extracted.variables,
      classVariables: extracted.classVariables,
      objectVariables: extracted.objectVariables,
      arrayVariables: extracted.arrayVariables,
      warnings: [...functionExpanded.warnings, ...extracted.warnings, ...loopWarnings],
    },
    {
      _variables: loopExtracted.variables,
      _classVariables: loopExtracted.classVariables,
      _objectVariables: loopExtracted.objectVariables,
      _arrayVariables: loopExtracted.arrayVariables,
      _warnings: loopExtracted.warnings,
    },
  )

  // Return full preprocess payload including collected warnings.
  return {
    source: loopExtracted.cleanSource,
    variables: mergedVariables.variables,
    classVariables: mergedVariables.classVariables,
    objectVariables: mergedVariables.objectVariables,
    arrayVariables: mergedVariables.arrayVariables,
    warnings: mergedVariables.warnings,
  }
}

// Detect whether source contains any function tokens requiring expansion.
function shouldRunFunctionExpansion(source: string): boolean {
  // Return true when source includes @fun definition/call markers.
  return containsTokenOutsideLiterals(source, '@fun:')
    || containsTokenOutsideLiterals(source, '@fun.')
}

// Detect whether loop/if scanner expansion is required for current source.
function shouldRunLoopExpansion(source: string): boolean {
  // Return true only for real loop/if/placeholder syntax markers.
  return containsTokenOutsideLiterals(source, '@for')
    || containsKeywordDelimiterOutsideLiterals(source, 'for', '(')
    || containsTokenOutsideLiterals(source, '@if')
    || containsKeywordDelimiterOutsideLiterals(source, 'elseif', '(')
    || containsKeywordDelimiterOutsideLiterals(source, 'else', '{')
    || containsLoopPlaceholderOutsideLiterals(source)
}

// Detect token existence while ignoring comment and string ranges.
function containsTokenOutsideLiterals(source: string, token: string): boolean {
  let quote: "'" | '"' | null = null
  let inLineComment = false
  let inBlockComment = false

  // Scan source and return true on first token match.
  for (let index = 0; index < source.length; index += 1) {
    const char = source[index]
    const next = source[index + 1] ?? ''
    const previous = index > 0 ? source[index - 1] : ''

    // Keep line comments opaque during token checks.
    if (inLineComment) {
      if (char === '\n') {
        inLineComment = false
      } else {
        // Continue line-comment scan.
      }
      continue
    } else if (inBlockComment) {
      // Keep block comments opaque during token checks.
      if (char === '*' && next === '/') {
        inBlockComment = false
        index += 1
      } else {
        // Continue block-comment scan.
      }
      continue
    } else {
      // Continue scanner outside comments.
    }

    // Keep quoted strings opaque during token checks.
    if (quote) {
      if (char === quote && previous !== '\\') {
        quote = null
      } else {
        // Continue quoted-string scan.
      }
      continue
    } else {
      // Continue scanner outside quotes.
    }

    // Enter comment/quote mode when delimiters are encountered.
    if (char === '/' && next === '/') {
      inLineComment = true
      index += 1
      continue
    } else if (char === '/' && next === '*') {
      inBlockComment = true
      index += 1
      continue
    } else if (char === '"' || char === "'") {
      quote = char
      continue
    } else {
      // Continue direct token checks.
    }

    // Return true when token matches at current index.
    if (source.slice(index, index + token.length) === token) {
      return true
    } else {
      // Continue scanner when token does not match.
    }
  }

  // Return false when token is absent outside literals.
  return false
}

// Detect `keyword ... delimiter` tokens outside comments/strings.
function containsKeywordDelimiterOutsideLiterals(source: string, keyword: string, delimiter: '(' | '{'): boolean {
  let quote: "'" | '"' | null = null
  let inLineComment = false
  let inBlockComment = false

  // Scan source once and return true on first keyword+delimiter match.
  for (let index = 0; index < source.length; index += 1) {
    const char = source[index]
    const next = source[index + 1] ?? ''
    const previous = index > 0 ? source[index - 1] : ''

    // Keep line comments opaque during token checks.
    if (inLineComment) {
      if (char === '\n') {
        inLineComment = false
      } else {
        // Continue line-comment scan.
      }
      continue
    } else if (inBlockComment) {
      // Keep block comments opaque during token checks.
      if (char === '*' && next === '/') {
        inBlockComment = false
        index += 1
      } else {
        // Continue block-comment scan.
      }
      continue
    } else {
      // Continue scanner outside comments.
    }

    // Keep quoted strings opaque during token checks.
    if (quote) {
      if (char === quote && previous !== '\\') {
        quote = null
      } else {
        // Continue quoted-string scan.
      }
      continue
    } else {
      // Continue scanner outside quotes.
    }

    // Enter comment/quote mode when delimiters are encountered.
    if (char === '/' && next === '/') {
      inLineComment = true
      index += 1
      continue
    } else if (char === '/' && next === '*') {
      inBlockComment = true
      index += 1
      continue
    } else if (char === '"' || char === "'") {
      quote = char
      continue
    } else {
      // Continue keyword+delimiter checks.
    }

    // Skip positions where keyword does not match.
    if (source.slice(index, index + keyword.length) !== keyword) {
      continue
    } else {
      // Continue with boundary and delimiter validation.
    }

    const before = index > 0 ? source[index - 1] : ''
    // Skip keywords that appear mid-identifier.
    if (/[a-zA-Z0-9_]/.test(before)) {
      continue
    } else {
      // Continue delimiter scan for boundary-safe keyword.
    }

    let cursor = index + keyword.length
    // Skip optional spaces between keyword and delimiter.
    while (cursor < source.length && isTokenWhitespace(source[cursor])) {
      cursor += 1
    }

    // Return true when delimiter appears after optional whitespace.
    if (source[cursor] === delimiter) {
      return true
    } else {
      // Continue scan when delimiter is missing.
    }
  }

  // Return false when keyword+delimiter is absent.
  return false
}

// Check if one character is token-separating whitespace.
function isTokenWhitespace(char: string): boolean {
  return char === ' ' || char === '\t' || char === '\n' || char === '\r' || char === '\f'
}

// Detect `$name` and `${expr}` placeholders outside comments/strings.
function containsLoopPlaceholderOutsideLiterals(source: string): boolean {
  let quote: "'" | '"' | null = null
  let inLineComment = false
  let inBlockComment = false

  // Scan source and return true on first placeholder marker.
  for (let index = 0; index < source.length; index += 1) {
    const char = source[index]
    const next = source[index + 1] ?? ''
    const previous = index > 0 ? source[index - 1] : ''

    // Keep line comments opaque during placeholder checks.
    if (inLineComment) {
      if (char === '\n') {
        inLineComment = false
      } else {
        // Continue line-comment scan.
      }
      continue
    } else if (inBlockComment) {
      // Keep block comments opaque during placeholder checks.
      if (char === '*' && next === '/') {
        inBlockComment = false
        index += 1
      } else {
        // Continue block-comment scan.
      }
      continue
    } else {
      // Continue scanner outside comments.
    }

    // Keep quoted strings opaque during placeholder checks.
    if (quote) {
      if (char === quote && previous !== '\\') {
        quote = null
      } else {
        // Continue quoted-string scan.
      }
      continue
    } else {
      // Continue scanner outside quotes.
    }

    // Enter comment/quote mode when delimiters are encountered.
    if (char === '/' && next === '/') {
      inLineComment = true
      index += 1
      continue
    } else if (char === '/' && next === '*') {
      inBlockComment = true
      index += 1
      continue
    } else if (char === '"' || char === "'") {
      quote = char
      continue
    } else {
      // Continue placeholder checks.
    }

    // Skip non-placeholder markers quickly.
    if (char !== '$') {
      continue
    } else {
      // Continue placeholder candidate validation.
    }

    const tokenHead = source[index + 1] ?? ''
    // Return true for `${...}` and `$name` placeholder heads.
    if (tokenHead === '{' || /[a-zA-Z_]/.test(tokenHead)) {
      return true
    } else {
      // Continue scanner when `$` is not placeholder syntax.
    }
  }

  // Return false when placeholder syntax is absent.
  return false
}
